import { useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Alert,
  Group,
  Image,
  Loader,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconPhoto, IconTrash, IconUpload } from "@tabler/icons-react";
import { gql } from "@apollo/client";
import { nanoid } from "nanoid";
import { useState } from "react";
import { usePlatformFileUpload } from "../../platform/files/PlatformFile";
import { CDN_BASE_URL } from "../../../../../utils/cdn";

const PLATFORMS_QUERY = gql`
  query platformsForImageUpload {
    platforms {
      id
      name
    }
  }
`;

interface PlatformOption {
  id: string;
  name: string;
}

interface ImageFieldUploadProps {
  value: string;
  onChange: (value: string) => void;
  onImmediateSave: (value: string) => Promise<void>;
  objectType: string;
  fieldKey: string;
}

export const ImageFieldUpload = ({
  value,
  onChange,
  onImmediateSave,
  objectType,
  fieldKey,
}: ImageFieldUploadProps) => {
  const { data, loading: platformsLoading } = useQuery<{
    platforms: PlatformOption[];
  }>(PLATFORMS_QUERY);
  const uploadToPlatform = usePlatformFileUpload();
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);

  const platforms = data?.platforms ?? [];
  const platformOptions = platforms.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const platformId = selectedPlatformId ?? platforms[0]?.id;
    if (!platformId) {
      notifications.show({
        title: "No platform selected",
        message: "Please select a platform to upload the image to",
        color: "red",
        autoClose: 5000,
      });
      return;
    }

    setUploading(true);
    try {
      const ALLOWED_EXTENSIONS = new Set([
        "png",
        "jpg",
        "jpeg",
        "gif",
        "webp",
        "svg",
        "avif",
      ]);
      const rawExt = file.name.split(".").pop()?.toLowerCase() ?? "png";
      const ext = ALLOWED_EXTENSIONS.has(rawExt) ? rawExt : "png";
      const safeObjectType = objectType.replace(/[^a-zA-Z0-9_-]/g, "");
      const safeFieldKey = fieldKey.replace(/[^a-zA-Z0-9_-]/g, "");
      const fileName = `uploads/images/${safeObjectType}_${safeFieldKey}_${nanoid(8)}.${ext}`;

      const result = await uploadToPlatform({
        platformId,
        name: fileName,
        type: "IMAGE",
        fileBlob: file,
        publish: true,
      });

      if (result?.prodCdnPath) {
        const fullUrl = `${CDN_BASE_URL}/${result.prodCdnPath}`;
        onChange(fullUrl);
        await onImmediateSave(fullUrl);
      }
    } catch (error) {
      notifications.show({
        title: "Upload failed",
        message:
          error instanceof Error && error.message.length < 200
            ? error.message
            : "Could not upload image",
        color: "red",
        autoClose: 5000,
      });
    } finally {
      setUploading(false);
    }
  };

  if (platformsLoading) {
    return <Loader size="sm" />;
  }

  if (platforms.length === 0) {
    return (
      <Alert color="yellow" title="No platforms available">
        Please create a platform first to upload images.
      </Alert>
    );
  }

  if (value) {
    return (
      <Stack gap="xs">
        <Image src={value} alt="Custom field image" maw={300} radius="md" />
        <Group gap="xs">
          <Text size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
            {value}
          </Text>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={() => {
              onChange("");
              onImmediateSave("");
            }}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Select
        label="Upload to platform"
        data={platformOptions}
        value={selectedPlatformId ?? platforms[0]?.id ?? null}
        onChange={setSelectedPlatformId}
        size="xs"
      />
      <Dropzone
        onDrop={handleDrop}
        accept={IMAGE_MIME_TYPE}
        multiple={false}
        loading={uploading}
        maxSize={10 * 1024 * 1024}
      >
        <Group
          justify="center"
          gap="xl"
          mih={80}
          style={{ pointerEvents: "none" }}
        >
          <Dropzone.Accept>
            <IconUpload size={32} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconPhoto size={32} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto size={32} stroke={1.5} />
          </Dropzone.Idle>
          <Text size="sm" c="dimmed">
            Drop an image here or click to browse
          </Text>
        </Group>
      </Dropzone>
    </Stack>
  );
};
